import React, { useState } from "react";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import * as FaIcons from "react-icons/fa";
import { ENV } from "../config/env";

const { FiMail, FiLoader } = FiIcons;
const { FaGoogle } = FaIcons;

export default function NCBAuth({ onAuthSuccess, onAuthError, setSessionDebug }) {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGoogleLogin = () => {
    const currentOrigin = window.location.origin + window.location.pathname;
    const googleAuthUrl = `${ENV.NCB_AUTH_API}/social-login?X-Database-Instance=${ENV.NCB_INSTANCE}&provider=google&redirect_url=${encodeURIComponent(currentOrigin)}`;
    window.location.href = googleAuthUrl;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const url = `${ENV.NCB_AUTH_API}/email-otp/send-verification-otp`;
      
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Database-Instance": ENV.NCB_INSTANCE,
          "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`
        },
        body: JSON.stringify({ email, type: "sign-in" }),
        credentials: 'include'
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned invalid response. Please check your configuration.");
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not send verification code.");
      
      setOtpSent(true);
    } catch (err) {
      console.error('[AUTH] Error:', err);
      setError(err.message);
      onAuthError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const verifyRes = await fetch(`${ENV.NCB_AUTH_API}/sign-in/email-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Database-Instance": ENV.NCB_INSTANCE,
          "Authorization": `Bearer ${ENV.NCB_SECRET_KEY}`
        },
        body: JSON.stringify({ email, otp }),
        credentials: 'include'
      });

      const verifyData = await verifyRes.json();

      if (!verifyRes.ok) {
        throw new Error(verifyData.message || "Invalid code.");
      }

      const finalUserData = verifyData.user;
      
      if (!finalUserData) {
        throw new Error('No user data returned from OTP verification');
      }

      if (setSessionDebug) {
        setSessionDebug(prev => ({
          ...prev,
          sessionSource: 'localStorage',
          loginSuccess: true
        }));
      }

      onAuthSuccess?.(finalUserData);
    } catch (err) {
      console.error('[AUTH] Verification error:', err);
      setError(err.message);
      onAuthError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-100 p-8 rounded-[2.5rem] shadow-2xl max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-black text-black mb-1">
          {otpSent ? "Authorize" : "Identity"}
        </h2>
        <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em]">
          {otpSent ? `Check ${email}` : "Choose Access Method"}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-2xl text-[10px] font-black uppercase text-center border border-red-100">
          {error}
        </div>
      )}

      {!otpSent ? (
        <div className="space-y-4">
          <button
            onClick={handleGoogleLogin}
            className="w-full py-4 bg-white border-2 border-gray-100 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center space-x-3 hover:border-black transition-all"
          >
            <SafeIcon icon={FaGoogle} className="text-lg" />
            <span>Continue with Google</span>
          </button>

          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100"></div>
            </div>
            <div className="relative flex justify-center text-[8px] font-black uppercase tracking-widest text-gray-300">
              <span className="bg-white px-2">Or secure email</span>
            </div>
          </div>

          <form onSubmit={handleSendOTP} className="space-y-4">
            <input
              type="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-black focus:bg-white transition-all text-sm font-bold outline-none"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-black text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center space-x-2 hover:bg-gray-900 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <span>Email Verification</span>
              )}
            </button>
          </form>
        </div>
      ) : (
        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <input
            type="text"
            placeholder="000000"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-black focus:bg-white text-center text-xl font-black tracking-[0.5em] outline-none"
            maxLength={6}
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-black text-white rounded-2xl font-black text-xs uppercase tracking-widest disabled:opacity-50"
          >
            {loading ? "Verifying..." : "Confirm Identity"}
          </button>
          <button
            type="button"
            onClick={() => {
              setOtpSent(false);
              setOtp("");
              setError("");
            }}
            className="w-full text-[10px] font-black uppercase tracking-widest text-gray-400 py-2 hover:text-black transition-colors"
          >
            Back to methods
          </button>
        </form>
      )}
    </div>
  );
}